// src/lib/components/molecules/Node/MetaspaceContent.tsx

import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState, AppDispatch } from '@center-pwa/store/store';
import { selectSphereId } from '@center-pwa/selectors/selectors';
import {
  selectAllMetaspaces,
  selectMetaspaceRefStatus,
  selectMetaspacesListStatus,
  fetchMetaspaceByRef,
} from '@center-pwa/reducers/metaspacesReducer';
import {
  EventProvider,
  MetaspaceGrid,
  MetaspaceDefinition,
  LoaderMolecule,
  MessageAtom,
  useApiClientsOrNull,
} from 'design-system/my-lib';
import type { SpaceDocument } from 'types/space';
import SpaceHost from './SpaceHost';
import './MetaspaceContent.scss';

interface MetaspaceContentProps {
  metaspaceId: string;
  onMetaspaceIdChange: (metaspaceId: string) => void; // Function to pass metaspaceId up
  toolbarOpen?: boolean;
  onToolbarOpenChange?: (isOpen: boolean) => void;
  toolbarActiveItem?: any;
  onToolbarActiveItemChange?: (item: any) => void;
}

const MetaspaceContent: React.FC<MetaspaceContentProps> = ({
  metaspaceId,
  onMetaspaceIdChange,
  toolbarOpen = false,
  onToolbarOpenChange,
  toolbarActiveItem = 'chatbox',
  onToolbarActiveItemChange,
}) => {
  const metaspaces = useSelector(selectAllMetaspaces);
  const sphereId = useSelector(selectSphereId);
  const dispatch = useDispatch<AppDispatch>();
  const apiClients = useApiClientsOrNull();
  const { t } = useTranslation();

  // Fetch the metaspace information based on the metaspaceId or system_name
  const metaspace = metaspaces.find(
    (metaspace) => metaspace.metaspace_id === metaspaceId || metaspace.system_name === metaspaceId
  );

  const listStatus = useSelector(selectMetaspacesListStatus);
  const refStatus = useSelector((state: RootState) =>
    selectMetaspaceRefStatus(state, metaspaceId)
  );

  // Resolve on demand when the route names a metaspace the init-time list did not
  // include — a deep link, a bookmark, or one deployed mid-session. Without this
  // the lookup above simply fails and the component renders nothing at all, which
  // is indistinguishable from "still loading" and from "you have no access"
  // (EAINT-818).
  //
  // Only once per ref: `refStatus` is set to 'loading' synchronously by the thunk's
  // pending case, so a re-render cannot queue a second request, and a resolved
  // 'not_found' is not retried on every render.
  useEffect(() => {
    if (metaspace) return;
    if (!metaspaceId || !apiClients?.schemaClient) return;
    // Wait for the init fetch to settle first, or a slow list and this lookup race
    // and the ref resolves twice.
    if (listStatus === 'loading' || listStatus === 'idle') return;
    if (refStatus) return;
    dispatch(fetchMetaspaceByRef({ apiClient: apiClients.schemaClient, ref: metaspaceId }));
  }, [metaspace, metaspaceId, apiClients?.schemaClient, listStatus, refStatus, dispatch]);

  // Notify parent component about the current metaspaceId
  useEffect(() => {
    if (metaspaceId) {
      onMetaspaceIdChange(metaspaceId);
    }
  }, [metaspaceId, onMetaspaceIdChange]);

  // `metaspace.definition` is either the legacy `layout[]` shape or a `space/v1` document — routed
  // by `formatVersion` below. The legacy path (type, render branch) is byte-untouched.
  const rawDefinition = metaspace ? metaspace.definition : null;
  const isSpaceDocument = (rawDefinition as SpaceDocument | null)?.formatVersion === 'space/v1';
  const definition: MetaspaceDefinition | null = !isSpaceDocument
    ? (rawDefinition as MetaspaceDefinition | null)
    : null;
  // `resolution` is a sibling column to `definition` on `metaspace_versions` (EAINT-891), not part
  // of the JSONB payload — attach it here so `useCompositionResolver` sees it on `document`.
  // MEMOIZED, not computed inline: `SpaceHostProps.document` requires a referentially stable
  // object (see its doc comment). A fresh literal per render makes `useCompositionResolver`'s
  // `resolved.document !== document` permanently true, so every unrelated re-render (a store
  // update, a toolbar toggle) would flip the host back to its loader, unmount the whole Space
  // subtree and re-fetch every composition ref.
  const rawSpaceDocument = isSpaceDocument ? (rawDefinition as SpaceDocument) : null;
  const metaspaceResolution = metaspace?.resolution;
  const spaceDocument: SpaceDocument | null = useMemo(
    () => (rawSpaceDocument ? { ...rawSpaceDocument, resolution: metaspaceResolution ?? undefined } : null),
    [rawSpaceDocument, metaspaceResolution]
  );

  // Console log to test showMetasphereToolbar setting accessibility
  useEffect(() => {
    if (definition?.settings?.showMetasphereToolbar !== undefined) {
      console.log('showMetasphereToolbar setting:', definition.settings.showMetasphereToolbar);
    }
  }, [definition]);

  const showHeader = metaspace?.system_name === 'center_task_list';

  // Every not-yet-resolved case rendered explicitly. This used to be a bare
  // `metaspace && (...)`, so a miss returned falsy and painted an empty page with
  // no spinner, no message and no network request — the user could not tell a
  // missing metaspace from a denied one from a slow one (EAINT-818).
  if (!metaspace) {
    if (listStatus === 'loading' || listStatus === 'idle' || refStatus === 'loading') {
      return (
        <div className="metaspace-content metaspace-content--resolving">
          <LoaderMolecule variant="inline" caption={t('common.loader.metaspace')} />
        </div>
      );
    }

    // A 404 from the list route and a row withheld by its permission filter are
    // indistinguishable from here, so both read as "not available" rather than
    // claiming the metaspace does not exist.
    const severity = refStatus === 'error' || listStatus === 'failed' ? 'error' : 'warn';
    let message: string;
    if (refStatus === 'forbidden') {
      message = t('metaspace.notAvailable.forbidden', {
        defaultValue: 'You do not have access to this metaspace.',
      });
    } else if (refStatus === 'error' || listStatus === 'failed') {
      message = t('metaspace.notAvailable.error', {
        defaultValue: 'This metaspace could not be loaded. Try again shortly.',
      });
    } else {
      message = t('metaspace.notAvailable.missing', {
        metaspace: metaspaceId,
        defaultValue: `'{{metaspace}}' is not available on this sphere.`,
      });
    }

    return (
      <div className="metaspace-content metaspace-content--unavailable">
        <MessageAtom severity={severity} text={message} />
      </div>
    );
  }

  return (
    metaspace && (
      <div className="metaspace-content">
        {showHeader && (
          <header className="metaspace-page-header">
            <h1 className="metaspace-page-header__title">{metaspace.name}</h1>
            {metaspace.description && (
              <p className="metaspace-page-header__subtitle">{metaspace.description}</p>
            )}
          </header>
        )}
        <EventProvider>
          {/* Gate on sphereId so the grid mounts once with its final key. sphereId comes
              from Redux and lags the URL (undefined → id on entry); without this gate the
              key flips `${metaspaceId}-undefined` → `${metaspaceId}-<id>`, remounting the
              whole grid (AtlasComponent/AtlasTree) and re-fetching the context tree. */}
          {definition && sphereId && (
            <MetaspaceGrid
              key={`${metaspaceId}-${sphereId}`}
              definition={definition}
              toolbarOpen={toolbarOpen}
              onToolbarOpenChange={onToolbarOpenChange}
              toolbarActiveItem={toolbarActiveItem}
              onToolbarActiveItemChange={onToolbarActiveItemChange}
            />
          )}
          {spaceDocument && sphereId && (
            <SpaceHost
              key={`${metaspaceId}-${sphereId}`}
              document={spaceDocument}
              metaspaceId={metaspaceId}
              sphereId={sphereId}
              toolbarOpen={toolbarOpen}
              onToolbarOpenChange={onToolbarOpenChange}
              toolbarActiveItem={toolbarActiveItem}
              onToolbarActiveItemChange={onToolbarActiveItemChange}
            />
          )}
        </EventProvider>
      </div>
    )
  );
};

export default MetaspaceContent;
